// ============================================================================
// A task that is not about one building (TCSD-0444, TCSD-0450).
//
// Ben, 8 Oct, option B: the building stays the first question on a new task,
// and "Not about one building" is a choice at the top of the list, never a
// blank. A missing building is always deliberate, never forgotten, so building
// work cannot drop off its building's Tasks tab by accident. The database has
// always allowed a task with no building; only the form and Ask AI asked.
// ============================================================================

/** The words a person reads wherever a task has no building. */
export const NO_BUILDING = 'Not about one building';

/** The value the choice carries in a building list. Never written anywhere. */
export const NO_BUILDING_VALUE = '__no_building__';

export interface BuildingPick<F> {
  facility: F | null;
  noBuilding: boolean;
}

/** What a choice in the building list means. An empty value is no choice yet. */
export function pickBuilding<F extends { facility_id: string }>(
  value: string,
  facilities: readonly F[],
): BuildingPick<F> {
  if (value === NO_BUILDING_VALUE) return { facility: null, noBuilding: true };
  return { facility: facilities.find((f) => f.facility_id === value) ?? null, noBuilding: false };
}

/** A new task saves once it says what to do and its building question is answered. */
export function canCreateTask(form: { description: string; facility: unknown; noBuilding: boolean }): boolean {
  if (!form.description.trim()) return false;
  return form.facility != null || form.noBuilding;
}

/** The building a new task is saved with: the one picked, or none when "Not about one building" was. */
export function taskBuilding(form: {
  facility: { facility_id: string; account_id: string | null } | null;
  noBuilding: boolean;
}): { facility_id: string | null; account_id: string | null } {
  if (form.noBuilding) return { facility_id: null, account_id: null };
  if (!form.facility) throw new Error('Choose a building, or "Not about one building".');
  return { facility_id: form.facility.facility_id, account_id: form.facility.account_id ?? null };
}
